import api from "./axiox";

// Default global discount is 0 (no discount until fetched from GET /api/Discount/Get)
let cachedGlobalDiscount = 0;
let lastFetchTime = 0;
const CACHE_TTL_MS = 10000; // 10 seconds cache to reflect admin dashboard changes rapidly

/**
 * Fetch the configured global discount from GET /api/Discount/Get
 * Returns the numeric discountAmount (e.g. 1000, or 0 if disabled)
 */
export const getGlobalDiscount = async (forceRefresh = false) => {
  const now = Date.now();
  if (!forceRefresh && lastFetchTime > 0 && now - lastFetchTime < CACHE_TTL_MS) {
    return cachedGlobalDiscount;
  }

  try {
    const response = await api.get("/Discount/Get");
    if (response?.data && typeof response.data.discountAmount !== "undefined") {
      cachedGlobalDiscount = Math.max(0, Number(response.data.discountAmount) || 0);
      lastFetchTime = now;
      return cachedGlobalDiscount;
    }
  } catch (err) {
    console.warn("[productPriceApi] Could not fetch global discount from server.", err?.message);
  }

  return cachedGlobalDiscount;
};

/**
 * Calculate pricing for any product given its actual MRP and the global discount amount.
 * Selling Price = Math.max(0, actualMrp - discountAmount)
 * Never produces a negative price.
 * If discountAmount is 0, discount is disabled and selling price = actualMrp.
 */
export const calculateProductPrice = (actualMrp, discountAmount = cachedGlobalDiscount) => {
  const mrp = Number(actualMrp) || 0;
  const discount = Math.max(0, Number(discountAmount) || 0);

  if (mrp <= 0) {
    return {
      mrp: 0,
      discountAmount: 0,
      effectivePrice: 0,
      isDiscountActive: false,
      savingsAmount: 0,
      discountPercentage: 0
    };
  }

  // Selling price formula: Math.Max(0, actualMrp - discountAmount)
  const actualDiscount = Math.min(mrp, discount);
  const effectivePrice = Math.max(0, mrp - actualDiscount);
  const isDiscountActive = actualDiscount > 0;
  const discountPercentage = mrp > 0 && isDiscountActive ? Math.round((actualDiscount / mrp) * 100) : 0;

  return {
    mrp,
    discountAmount: actualDiscount,
    effectivePrice,
    isDiscountActive,
    savingsAmount: actualDiscount,
    discountPercentage
  };
};

/**
 * Compatibility wrapper for fetching pricing data.
 * Returns an object containing the globalDiscount.
 */
export const getProductPrices = async (forceRefresh = false) => {
  const discount = await getGlobalDiscount(forceRefresh);
  return { globalDiscount: discount, discountAmount: discount };
};

/**
 * Universal pricing helper that accepts any combination of parameters:
 * (catalog, sku, actualMrp, price) or (actualMrp, catalog) or (sku, actualMrp, catalog)
 * and calculates Math.max(0, actualMrp - discountAmount) strictly using the product's actual MRP.
 */
export const getPriceForSku = (...args) => {
  let rawMrp = null;
  let catalogOrDiscount = null;

  for (const arg of args) {
    if (typeof arg === "number") {
      if (rawMrp === null) {
        rawMrp = arg;
      } else if (catalogOrDiscount === null) {
        catalogOrDiscount = arg;
      }
    } else if (typeof arg === "object" && arg !== null) {
      catalogOrDiscount = arg;
    } else if (typeof arg === "string" && !isNaN(Number(arg)) && Number(arg) > 0) {
      if (rawMrp === null) {
        rawMrp = Number(arg);
      }
    }
  }

  const numMrp = Number(rawMrp) || 0;

  let discount = cachedGlobalDiscount;
  if (typeof catalogOrDiscount === "number") {
    discount = catalogOrDiscount;
  } else if (catalogOrDiscount && typeof catalogOrDiscount.globalDiscount === "number") {
    discount = catalogOrDiscount.globalDiscount;
  } else if (catalogOrDiscount && typeof catalogOrDiscount.discountAmount === "number") {
    discount = catalogOrDiscount.discountAmount;
  }

  return calculateProductPrice(numMrp, discount);
};

const productPriceApi = {
  getGlobalDiscount,
  calculateProductPrice,
  getProductPrices,
  getPriceForSku
};

export default productPriceApi;
